/**
 * Filename: CurriculumContentPreview.tsx
 * Description: Shared read-only level, set, and item explorer for learner content activities.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-07-28
 * Updated: 2026-07-30
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ShinQLoader } from './ShinQLoader';

const SET_SIZE = 10;
const READING_SET_SIZE = 5;
const CHOICE_COUNT = 4;
const EXAM_TEST_LEVELS = ['beginner', 'intermediate', 'advanced', 'mastery'] as const;
const TOEIC_PARTS = [
  { id: 1, section: 'Listening', title: 'Photographs', questions: 6, detail: 'Look at a picture, listen to four statements, and choose the best description.' },
  { id: 2, section: 'Listening', title: 'Question-Response', questions: 25, detail: 'Listen to a question or statement and choose the best response.' },
  { id: 3, section: 'Listening', title: 'Conversations', questions: 39, detail: 'Listen to 13 conversations and answer three questions for each.' },
  { id: 4, section: 'Listening', title: 'Talks and Announcements', questions: 30, detail: 'Listen to 10 short talks and answer three questions for each.' },
  { id: 5, section: 'Reading', title: 'Incomplete Sentences', questions: 30, detail: 'Complete sentences using the correct grammar or vocabulary choice.' },
  { id: 6, section: 'Reading', title: 'Text Completion', questions: 16, detail: 'Complete passage blanks with a word, phrase, or entire sentence.' },
  { id: 7, section: 'Reading', title: 'Reading Comprehension', questions: 54, detail: 'Answer questions about single, double, and triple documents.' },
] as const;
const TOEFL_SECTIONS = [
  { id: 1, title: 'Reading', duration: '35 minutes', summary: 'Read university-level academic passages.', tasks: ['Main idea', 'Details', 'Vocabulary in context', 'Inference', 'Sentence insertion', 'Summary'] },
  { id: 2, title: 'Listening', duration: '36 minutes', summary: 'Listen to campus conversations and academic lectures.', tasks: ['Main idea', 'Details', "Speaker's purpose", 'Organization', 'Inference'] },
  { id: 3, title: 'Speaking', duration: '16 minutes', summary: 'Record spoken responses into a microphone.', tasks: ['Independent speaking', 'Integrated speaking · Task 2', 'Integrated speaking · Task 3', 'Integrated speaking · Task 4'] },
  { id: 4, title: 'Writing', duration: '29 minutes', summary: 'Write academic responses based on prompts and sources.', tasks: ['Integrated Writing', 'Writing for an Academic Discussion'] },
] as const;
const IELTS_COMMON_SECTIONS = [
  { id: 1, title: 'Listening', duration: '30 minutes', summary: '40 questions across everyday conversation, monologue, academic discussion, and academic lecture.', tasks: ['Part 1 · Everyday conversation', 'Part 2 · Monologue', 'Part 3 · Academic discussion', 'Part 4 · Academic lecture'] },
  { id: 4, title: 'Speaking', duration: '11–14 minutes', summary: 'A face-to-face interview with a real examiner.', tasks: ['Part 1 · Introduction', 'Part 2 · Cue card', 'Part 3 · Abstract discussion'] },
] as const;
const MODE_DETAILS = {
  today: { title: "Today's Lesson", itemLabel: 'Activity', setLabel: 'Daily Lesson Set' },
  vocabulary: { title: 'Vocabulary', itemLabel: 'Card', setLabel: 'Vocabulary Set' },
  flashcards: { title: 'Flashcards', itemLabel: 'Card', setLabel: 'Flashcard Set' },
  listening: { title: 'Listening', itemLabel: 'Question', setLabel: 'Listening Set' },
  spelling: { title: 'Spelling Bee', itemLabel: 'Word', setLabel: 'Spelling Set' },
  ai: { title: 'AI Questions', itemLabel: 'Question', setLabel: 'AI Question Set' },
  pronunciation: { title: 'Pronunciation', itemLabel: 'Prompt', setLabel: 'Pronunciation Set' },
  reading: { title: 'Reading', itemLabel: 'Question', setLabel: 'Reading Set' },
  dictation: { title: 'Dictation', itemLabel: 'Question', setLabel: 'Dictation Set' },
  translation: { title: 'Translation', itemLabel: 'Question', setLabel: 'Translation Set' },
  mistakes: { title: 'Mistakes', itemLabel: 'Review', setLabel: 'Mistake Review Set' },
  mixed: { title: 'Mixed Practice', itemLabel: 'Activity', setLabel: 'Mixed Set' },
  tournament: { title: 'Tournaments', itemLabel: 'Round', setLabel: 'Tournament Set' },
  offline: { title: 'Offline Mode', itemLabel: 'Activity', setLabel: 'Offline Set' },
  test: { title: 'Tests', itemLabel: 'Question', setLabel: 'Test Set' },
  'ielts-academic': { title: 'IELTS Academic', itemLabel: 'Task', setLabel: 'IELTS Academic Test' },
  'ielts-general': { title: 'IELTS General Training', itemLabel: 'Task', setLabel: 'IELTS General Training Test' },
  toeic: { title: 'TOEIC Readiness', itemLabel: 'Question', setLabel: 'TOEIC Practice Set' },
  toefl: { title: 'TOEFL iBT Readiness', itemLabel: 'Task', setLabel: 'TOEFL iBT Practice Test' },
  'assessment-reading': { title: 'English Level Check · Reading', itemLabel: 'Question', setLabel: 'Reading Check Set' },
  'assessment-listening': { title: 'English Level Check · Listening', itemLabel: 'Question', setLabel: 'Listening Check Set' },
  'assessment-writing': { title: 'English Level Check · Writing', itemLabel: 'Question', setLabel: 'Writing Check Set' },
  'assessment-speaking': { title: 'English Level Check · Speaking', itemLabel: 'Question', setLabel: 'Speaking Check Set' },
  'assessment-grammar': { title: 'English Level Check · Grammar', itemLabel: 'Question', setLabel: 'Grammar Check Set' },
  'assessment-vocabulary': { title: 'English Level Check · Vocabulary', itemLabel: 'Question', setLabel: 'Vocabulary Check Set' },
} as const;
const LEVELS = [
  { id: 'pre_basic', title: 'Pre-Basic', detail: 'First concrete words and simple activities' },
  { id: 'beginner', title: 'Basic', detail: 'Essential foundations and everyday English' },
  { id: 'intermediate', title: 'Intermediate', detail: 'Natural conversation and more detailed language' },
  { id: 'advanced', title: 'Advanced', detail: 'Complex, precise, and professional English' },
  { id: 'mastery', title: 'Mastery', detail: 'Nuanced academic and expert-level English' },
] as const;

type Level = typeof LEVELS[number]['id'];
export type CurriculumMode = keyof typeof MODE_DETAILS;
type AdminApi = (path: string, init?: RequestInit) => Promise<unknown>;

interface CurriculumVocabularyItem {
  vocabId: string;
  englishWord: string;
  pronunciation: string;
  partOfSpeech: string;
  proficiencyLevel: Level;
  difficulty: number;
  senses?: Array<{ japaneseTranslation?: string; japaneseDefinition?: string; definition?: string }>;
  images?: Array<{ thumbnail256Url?: string; mobile512Url?: string }>;
  audioAmericanUrl?: string;
  audioBritishUrl?: string;
  adminQuestionOverrides?: Partial<Record<CurriculumMode, QuestionPreview>>;
}

interface VocabularyResponse {
  vocabulary?: CurriculumVocabularyItem[];
  total?: number;
}

interface CurriculumReadingPassage {
  passageId: string;
  title: string;
  text: string;
  level: Level;
  genre: string;
  audience: string;
  wordCount: number;
  estimatedReadingSeconds: number;
  copyrightBasis: 'original' | 'licensed' | 'public_domain';
  licenseReference?: string;
  translationSegments?: Array<{ segmentNumber: number; englishText: string; japaneseReference: string; source: 'human_reviewed' | 'aws_translate' }>;
  status: 'published';
}

interface ReadingPassagesResponse { passages?: CurriculumReadingPassage[] }

interface CurriculumRoute {
  level?: Level;
  setNumber?: number;
}

export function CurriculumContentPreview({ api, mode, writable = true }: { api: AdminApi; mode: CurriculumMode; writable?: boolean }) {
  const isLevelCheck = mode.startsWith('assessment-');
  const isDailyLesson = mode === 'today';
  const isIelts = mode === 'ielts-academic' || mode === 'ielts-general';
  const isExamReadiness = isIelts || mode === 'toeic' || mode === 'toefl';
  const isToeic = mode === 'toeic';
  const isToefl = mode === 'toefl';
  const isStructuredExam = isToeic || isToefl || isIelts;
  const isReading = mode === 'reading';
  const isDirectLevelMode = isLevelCheck || isDailyLesson || (isExamReadiness && !isStructuredExam);
  const [route, setRoute] = useState<CurriculumRoute>(() => curriculumRouteFromHash(window.location.hash, mode));
  const [totals, setTotals] = useState<Partial<Record<Level, number>>>({});
  const [items, setItems] = useState<CurriculumVocabularyItem[]>([]);
  const [readingPassages, setReadingPassages] = useState<CurriculumReadingPassage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, QuestionPreview>>({});
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const syncRoute = () => setRoute(curriculumRouteFromHash(window.location.hash, mode));
    window.addEventListener('hashchange', syncRoute);
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.removeEventListener('hashchange', syncRoute);
      window.removeEventListener('popstate', syncRoute);
    };
  }, [mode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!route.level) {
        const responses = await Promise.all(LEVELS.map(async ({ id }) => {
          if (isReading) {
            const response = await api(`/reading/passages?level=${id}&limit=50`) as ReadingPassagesResponse;
            return [id, response.passages?.length ?? 0] as const;
          }
          const response = await api(`/vocabulary?level=${id}&limit=1&offset=0`) as VocabularyResponse;
          return [id, response.total ?? 0] as const;
        }));
        setTotals(Object.fromEntries(responses) as Record<Level, number>);
        setItems([]);
        setReadingPassages([]);
      } else if (!route.setNumber && !isDirectLevelMode) {
        if (isReading) {
          const response = await api(`/reading/passages?level=${route.level}&limit=50`) as ReadingPassagesResponse;
          setTotals((current) => ({ ...current, [route.level!]: response.passages?.length ?? 0 }));
        } else {
          const response = await api(`/vocabulary?level=${route.level}&limit=1&offset=0`) as VocabularyResponse;
          setTotals((current) => ({ ...current, [route.level!]: response.total ?? 0 }));
        }
        setItems([]);
      } else if (!isStructuredExam) {
        if (isReading) {
          const response = await api(`/reading/passages?level=${route.level}&limit=50`) as ReadingPassagesResponse;
          const passages = response.passages ?? [];
          const offset = ((route.setNumber ?? 1) - 1) * READING_SET_SIZE;
          setTotals((current) => ({ ...current, [route.level!]: passages.length }));
          setReadingPassages(passages.slice(offset, offset + READING_SET_SIZE));
          setItems([]);
        } else {
          const offset = ((route.setNumber ?? 1) - 1) * SET_SIZE;
          const response = await api(`/vocabulary?level=${route.level}&limit=${SET_SIZE}&offset=${offset}`) as VocabularyResponse;
          setTotals((current) => ({ ...current, [route.level!]: response.total ?? 0 }));
          setItems(response.vocabulary ?? []);
        }
      } else {
        setItems([]);
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load spelling content.');
    } finally {
      setLoading(false);
    }
  }, [api, isDirectLevelMode, isReading, isStructuredExam, route.level, route.setNumber]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedLevel = LEVELS.find(({ id }) => id === route.level);
  const modeDetails = MODE_DETAILS[mode];
  const examSummary = getExamSummary(mode);
  const total = route.level ? totals[route.level] ?? 0 : 0;
  const setSize = isReading ? READING_SET_SIZE : SET_SIZE;
  const setCount = Math.ceil(total / setSize);
  const examTestNumber = route.level && route.level !== 'pre_basic' ? EXAM_TEST_LEVELS.indexOf(route.level) + 1 : 0;
  const toeicPart = isToeic && route.setNumber ? TOEIC_PARTS.find(({ id }) => id === route.setNumber) : undefined;
  const toeflSection = isToefl && route.setNumber ? TOEFL_SECTIONS.find(({ id }) => id === route.setNumber) : undefined;
  const ieltsSections = getIeltsSections(mode);
  const ieltsSection = isIelts && route.setNumber ? ieltsSections.find(({ id }) => id === route.setNumber) : undefined;
  const ieltsLabel = mode === 'ielts-academic' ? 'IELTS Academic' : 'IELTS General Training';
  const examCardLabel = isIelts ? ieltsLabel : mode === 'toefl' ? 'TOEFL iBT' : mode.toUpperCase();
  const title = ieltsSection
    ? `${ieltsLabel} Test ${examTestNumber} · ${ieltsSection.title}`
    : toeflSection
    ? `TOEFL iBT Test ${examTestNumber} · ${toeflSection.title}`
    : toeicPart
    ? `TOEIC Test ${examTestNumber} · Part ${toeicPart.id}: ${toeicPart.title}`
    : isExamReadiness && route.level
    ? `${isIelts ? ieltsLabel : mode === 'toefl' ? 'TOEFL iBT' : mode.toUpperCase()} Test ${examTestNumber}`
    : isDirectLevelMode && route.level
      ? `${selectedLevel?.title} ${modeDetails.title}`
    : route.setNumber
    ? `${selectedLevel?.title} ${modeDetails.title} · Set ${route.setNumber}`
    : route.level
      ? `${selectedLevel?.title} ${modeDetails.title} Sets`
      : examSummary
        ? `${modeDetails.title} Practice Tests`
        : `${modeDetails.title} Levels`;
  const breadcrumbs = useMemo(() => [
    { label: modeDetails.title, onClick: () => navigateToCurriculum(mode, {}) },
    ...(selectedLevel ? [{
      label: isExamReadiness ? `${isIelts ? ieltsLabel : mode === 'toefl' ? 'TOEFL iBT' : mode.toUpperCase()} Test ${examTestNumber}` : selectedLevel.title,
      onClick: () => navigateToCurriculum(mode, { level: selectedLevel.id }),
    }] : []),
    ...(route.setNumber && !isDirectLevelMode ? [{ label: isToeic ? `Part ${route.setNumber}` : isToefl ? toeflSection?.title ?? `Section ${route.setNumber}` : isIelts ? ieltsSection?.title ?? `Section ${route.setNumber}` : `Set ${route.setNumber}`, onClick: undefined }] : []),
  ], [mode, modeDetails.title, route.setNumber, selectedLevel]);

  const startEditing = () => {
    setDrafts(Object.fromEntries(items.map((item, index) => [
      item.vocabId,
      item.adminQuestionOverrides?.[mode] ?? buildQuestionPreview(mode, item, items, index),
    ])));
    setDeletedIds(new Set());
    setEditing(true);
  };

  const discardEditing = () => {
    setDrafts({});
    setDeletedIds(new Set());
    setEditing(false);
  };

  const saveSet = async () => {
    const activeItems = items.filter((item) => !deletedIds.has(item.vocabId));
    const complete = activeItems.length === SET_SIZE && activeItems.every((item) => {
      const draft = drafts[item.vocabId];
      return draft?.type.trim() && draft.prompt.trim() && draft.answer.trim()
        && draft.choices.length > 0 && draft.choices.every((choice) => choice.trim())
        && draft.choices.includes(draft.answer);
    });
    if (!complete) {
      const discard = window.confirm('You need to complete all 10 questions before saving. Select OK to discard your changes, or Cancel to continue editing.');
      if (discard) discardEditing();
      return;
    }
    setSaving(true);
    setError('');
    try {
      await Promise.all(activeItems.map((item) => api(`/admin/vocabulary/${encodeURIComponent(item.vocabId)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          action: 'edit',
          adminQuestionOverrides: { ...(item.adminQuestionOverrides ?? {}), [mode]: drafts[item.vocabId] },
        }),
      })));
      discardEditing();
      await load();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save this question set.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="panel curriculum-explorer">
      <div className="panel-heading">
        <div><p className="eyebrow">{examSummary ? 'Exam readiness blueprint' : 'Read-only learner preview'}</p><h2>{title}</h2></div>
        <p className="muted">{examSummary?.overview ?? (isReading ? 'The same published five-story sets and catalog ordering used by learners.' : 'The same level, 10-word set, and catalog ordering used by learners.')}</p>
      </div>
      <nav className="content-breadcrumbs" aria-label={`${modeDetails.title} content location`}>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && <span>/</span>}
            <button type="button" disabled={!crumb.onClick} onClick={crumb.onClick}>{crumb.label}</button>
          </React.Fragment>
        ))}
      </nav>
      {error && <div className="alert error">{error}</div>}
      {loading && <div className="workspace-empty"><ShinQLoader message={`Loading ${modeDetails.title.toLowerCase()} content…`} /></div>}
      {!loading && !error && !route.level && isExamReadiness && (
        <div className="curriculum-grid level-grid">
          {EXAM_TEST_LEVELS.map((level, index) => (
            <button className="curriculum-card" key={level} onClick={() => navigateToCurriculum(mode, { level })}>
              <span className="curriculum-number">{index + 1}</span>
              <strong>{examCardLabel} Test {index + 1}</strong>
              <p>{examSummary?.testDescriptions[index]}</p>
              <small>{examSummary?.testDetail}</small>
            </button>
          ))}
        </div>
      )}
      {!loading && !error && !route.level && !isExamReadiness && (
        <div className="curriculum-grid level-grid">
          {LEVELS.map((level) => {
            const levelTotal = totals[level.id] ?? 0;
            return (
              <button className="curriculum-card" key={level.id} onClick={() => navigateToCurriculum(mode, { level: level.id })}>
                <span className="curriculum-number">{isDirectLevelMode ? SET_SIZE : Math.ceil(levelTotal / setSize)}</span>
                <strong>{level.title}</strong><p>{level.detail}</p>
                <small>
                  {isLevelCheck
                    ? `${SET_SIZE} assessment questions`
                    : isDailyLesson
                      ? `${SET_SIZE} questions · refreshed daily`
                      : isReading ? `${levelTotal} published stories · ${Math.ceil(levelTotal / READING_SET_SIZE)} sets` : `${levelTotal} words · ${Math.ceil(levelTotal / SET_SIZE)} sets`}
                </small>
              </button>
            );
          })}
        </div>
      )}
      {!loading && !error && route.level && !route.setNumber && !isDirectLevelMode && (
        isToeic ? <ToeicPartGrid mode={mode} level={route.level} /> : isToefl ? <ToeflSectionGrid mode={mode} level={route.level} /> : isIelts ? <IeltsSectionGrid mode={mode} level={route.level} sections={ieltsSections} /> : <div className="curriculum-grid set-grid">
          {Array.from({ length: setCount }, (_, index) => {
            const setNumber = index + 1;
            const wordsInSet = Math.min(setSize, Math.max(0, total - index * setSize));
            return (
              <button className="curriculum-card set-preview-card" key={setNumber} onClick={() => navigateToCurriculum(mode, { level: route.level, setNumber })}>
                <span className="curriculum-number">{setNumber}</span>
                <strong>{isReading ? (setNumber === 3 ? 'Fairy Tales' : `Reading Set ${setNumber}`) : `${modeDetails.setLabel} ${setNumber}`}</strong><p>{wordsInSet} {isReading ? 'stories' : `${modeDetails.itemLabel.toLowerCase()} items`}</p>
                <small>Open complete answer key</small>
              </button>
            );
          })}
        </div>
      )}
      {!loading && !error && isToeic && toeicPart && <ToeicPartPreview part={toeicPart} testNumber={examTestNumber} />}
      {!loading && !error && isToefl && toeflSection && <ToeflSectionPreview section={toeflSection} testNumber={examTestNumber} />}
      {!loading && !error && isIelts && ieltsSection && <IeltsSectionPreview mode={mode} section={ieltsSection} testNumber={examTestNumber} />}
      {!loading && !error && route.level && (route.setNumber || isDirectLevelMode) && (
        !isStructuredExam && <div className="curriculum-set-details">
          {isReading ? <ReadingPassagePreview passages={readingPassages} /> : <>
          <div className="curriculum-set-toolbar">
            <p>{editing ? `${items.length - deletedIds.size} of ${SET_SIZE} questions complete` : 'Read-only answer key'}</p>
            <div>
              {editing
                ? <><button className="secondary" type="button" onClick={discardEditing} disabled={saving}>Discard</button><button type="button" onClick={() => void saveSet()} disabled={saving}>{saving ? 'Saving…' : 'Save set'}</button></>
                : writable ? <button type="button" onClick={startEditing}>Edit questions</button> : null}
            </div>
          </div>
          {items.filter((item) => !deletedIds.has(item.vocabId)).map((item, index) => (
            <CurriculumItemCard
              item={item}
              items={items}
              index={index}
              itemLabel={modeDetails.itemLabel}
              mode={mode}
              question={editing ? drafts[item.vocabId] : undefined}
              editing={editing}
              onQuestionChange={(question) => setDrafts((current) => ({ ...current, [item.vocabId]: question }))}
              onDelete={() => setDeletedIds((current) => new Set([...current, item.vocabId]))}
              key={item.vocabId}
            />
          ))}
          {!items.length && <div className="workspace-empty">This set has no published words.</div>}
          </>}
        </div>
      )}
    </section>
  );
}

function ReadingPassagePreview({ passages }: { passages: CurriculumReadingPassage[] }) {
  if (!passages.length) return <div className="workspace-empty">This set has no published reading stories.</div>;
  return <div className="reading-passage-preview-list">{passages.map((passage, index) => (
    <article className="curriculum-detail-card reading-passage-preview" key={passage.passageId}>
      <div className="curriculum-item-number"><small>Story</small>{index + 1}</div>
      <div className="curriculum-item-content">
        <div className="word-row curriculum-word-row"><div><h3>{passage.title}</h3><p className="pronunciation">{passage.wordCount} words · about {Math.max(1, Math.ceil(passage.estimatedReadingSeconds / 60))} min</p></div><span className="status-pill active curriculum-level-pill">Published</span></div>
        <div className="definition-grid definition-grid-three"><div><small>Genre</small><p>{passage.genre.replace(/_/g, ' ')}</p></div><div><small>Audience</small><p>{passage.audience}</p></div><div><small>Copyright basis</small><p>{passage.copyrightBasis.replace(/_/g, ' ')}</p></div></div>
        <section className="curriculum-answer-key"><div className="curriculum-question-heading"><div><small>Complete learner passage</small><strong>{passage.text}</strong></div></div>{passage.licenseReference ? <div className="curriculum-answer"><small>Source / license record</small><strong>{passage.licenseReference}</strong></div> : null}</section>
        <section className="curriculum-answer-key" aria-label={`Translation answer set for ${passage.title}`}><div className="curriculum-question-heading"><div><small>Translation answer set</small><strong>{passage.translationSegments?.length ?? 0} learner chunks</strong></div></div>{passage.translationSegments?.length ? passage.translationSegments.map((segment) => <div className="curriculum-answer" key={segment.segmentNumber}><small>Part {segment.segmentNumber} · English</small><strong>{segment.englishText}</strong><small>Reference Japanese · {segment.source === 'human_reviewed' ? 'Human reviewed' : 'AWS Translate draft'}</small><strong lang="ja">{segment.japaneseReference}</strong></div>) : <div className="alert error">Translation reference answers have not been prepared.</div>}</section>
        <code>{passage.passageId}</code>
      </div>
    </article>
  ))}</div>;
}

interface ExamSummary {
  overview: string;
  testDetail: string;
  testDescriptions: readonly string[];
}

function getExamSummary(mode: CurriculumMode): ExamSummary | undefined {
  const testDescriptions = [
    'Full-length baseline practice exam',
    'Full-length alternate practice exam',
    'Full-length advanced practice exam',
    'Full-length final readiness exam',
  ] as const;
  if (mode === 'ielts-academic' || mode === 'ielts-general') {
    return {
      overview: 'Four complete IELTS practice tests covering Listening, Reading, Writing, Speaking, timing, and band scoring.',
      testDetail: '4 sections · approximately 2 hours 45 minutes',
      testDescriptions,
    };
  }
  if (mode === 'toeic') {
    return {
      overview: 'Four complete TOEIC Listening and Reading practice tests following the official seven-part structure.',
      testDetail: '200 questions · approximately 120 minutes',
      testDescriptions,
    };
  }
  if (mode === 'toefl') {
    return {
      overview: 'Four complete TOEFL iBT practice tests covering academic Reading, Listening, Speaking, and Writing.',
      testDetail: '4 sections · approximately 2 hours',
      testDescriptions,
    };
  }
  return undefined;
}

function ToeicPartGrid({ mode, level }: { mode: CurriculumMode; level: Level }) {
  return <div className="toeic-blueprint">
    {(['Listening', 'Reading'] as const).map((section) => <section className="toeic-section" key={section}>
      <div className="toeic-section-heading"><h3>{section}</h3><strong>100 questions · {section === 'Listening' ? '~45 minutes' : '75 minutes'}</strong></div>
      <div className="curriculum-grid set-grid">{TOEIC_PARTS.filter((part) => part.section === section).map((part) => (
        <button className="curriculum-card set-preview-card" key={part.id} onClick={() => navigateToCurriculum(mode, { level, setNumber: part.id })}>
          <span className="curriculum-number">{part.id}</span><strong>Part {part.id} · {part.title}</strong><p>{part.detail}</p><small>{part.questions} required questions</small>
        </button>
      ))}</div>
    </section>)}
    <div className="toeic-total"><strong>200 questions</strong><span>120 minutes total</span></div>
  </div>;
}

function ToeicPartPreview({ part, testNumber }: { part: typeof TOEIC_PARTS[number]; testNumber: number }) {
  return <div className="toeic-part-preview">
    <section className="toeic-requirement-card"><div><p className="eyebrow">TOEIC Test {testNumber} · {part.section}</p><h3>Part {part.id}: {part.title}</h3><p>{part.detail}</p></div><strong>{part.questions} required questions</strong></section>
    <div className="alert error">0 of {part.questions} dedicated TOEIC questions are complete. This part cannot be published yet.</div>
    <div className="toeic-question-slots">{Array.from({ length: part.questions }, (_, index) => <div className="toeic-question-slot" key={index}><span>{index + 1}</span><strong>Question {index + 1}</strong><small>Content required</small></div>)}</div>
  </div>;
}

function ToeflSectionGrid({ mode, level }: { mode: CurriculumMode; level: Level }) {
  return <div className="toeic-blueprint">
    <div className="curriculum-grid level-grid">{TOEFL_SECTIONS.map((section) => (
      <button className="curriculum-card" key={section.id} onClick={() => navigateToCurriculum(mode, { level, setNumber: section.id })}>
        <span className="curriculum-number">{section.id}</span><strong>{section.title}</strong><p>{section.summary}</p><small>{section.duration}</small>
      </button>
    ))}</div>
    <div className="toeic-total"><strong>TOEFL iBT</strong><span>Approximately 2 hours total</span></div>
  </div>;
}

function ToeflSectionPreview({ section, testNumber }: { section: typeof TOEFL_SECTIONS[number]; testNumber: number }) {
  return <div className="toeic-part-preview">
    <section className="toeic-requirement-card"><div><p className="eyebrow">TOEFL iBT Test {testNumber}</p><h3>{section.title}</h3><p>{section.summary}</p></div><strong>{section.duration}</strong></section>
    <div className="toeic-question-slots">{section.tasks.map((task, index) => <div className="toeic-question-slot" key={task}><span>{index + 1}</span><strong>{task}</strong><small>Content required</small></div>)}</div>
    <div className="alert error">This section cannot be published until its passages, prompts, audio, answers, and scoring materials are complete.</div>
  </div>;
}

interface IeltsSection {
  id: number;
  title: string;
  duration: string;
  summary: string;
  tasks: readonly string[];
}

function getIeltsSections(mode: CurriculumMode): IeltsSection[] {
  const academic = mode === 'ielts-academic';
  return [
    IELTS_COMMON_SECTIONS[0],
    {
      id: 2,
      title: 'Reading',
      duration: '60 minutes',
      summary: academic
        ? '40 questions using research articles and academic passages about science, history, technology, and the environment.'
        : '40 questions using advertisements, notices, policies, newspapers, and workplace documents.',
      tasks: ['Multiple choice', 'True / False / Not Given', 'Matching headings', 'Sentence completion', 'Summary completion'],
    },
    {
      id: 3,
      title: 'Writing',
      duration: '60 minutes',
      summary: academic ? 'Write an information report and an academic essay.' : 'Write a practical letter and an essay.',
      tasks: academic
        ? ['Task 1 · Describe a chart, graph, table, map, or process · minimum 150 words', 'Task 2 · Essay · minimum 250 words']
        : ['Task 1 · Complaint, request, or invitation letter · minimum 150 words', 'Task 2 · Essay · minimum 250 words'],
    },
    IELTS_COMMON_SECTIONS[1],
  ];
}

function IeltsSectionGrid({ mode, level, sections }: { mode: CurriculumMode; level: Level; sections: IeltsSection[] }) {
  return <div className="toeic-blueprint">
    <div className="curriculum-grid level-grid">{sections.map((section) => (
      <button className="curriculum-card" key={section.id} onClick={() => navigateToCurriculum(mode, { level, setNumber: section.id })}>
        <span className="curriculum-number">{section.id}</span><strong>{section.title}</strong><p>{section.summary}</p><small>{section.duration}</small>
      </button>
    ))}</div>
    <div className="toeic-total"><strong>IELTS Band Score 1.0–9.0</strong><span>Approximately 2 hours 45 minutes</span></div>
  </div>;
}

function IeltsSectionPreview({ mode, section, testNumber }: { mode: CurriculumMode; section: IeltsSection; testNumber: number }) {
  const label = mode === 'ielts-academic' ? 'IELTS Academic' : 'IELTS General Training';
  return <div className="toeic-part-preview">
    <section className="toeic-requirement-card"><div><p className="eyebrow">{label} Test {testNumber}</p><h3>{section.title}</h3><p>{section.summary}</p></div><strong>{section.duration}</strong></section>
    <div className="toeic-question-slots">{section.tasks.map((task, index) => <div className="toeic-question-slot" key={task}><span>{index + 1}</span><strong>{task}</strong><small>Content required</small></div>)}</div>
    <div className="alert error">This IELTS section cannot be published until every prompt, passage, recording, answer, and band-scoring rubric is complete.</div>
  </div>;
}

function CurriculumItemCard({
  item,
  items,
  index,
  itemLabel,
  mode,
  question: editedQuestion,
  editing,
  onQuestionChange,
  onDelete,
}: {
  item: CurriculumVocabularyItem;
  items: CurriculumVocabularyItem[];
  index: number;
  itemLabel: string;
  mode: CurriculumMode;
  question?: QuestionPreview;
  editing: boolean;
  onQuestionChange: (question: QuestionPreview) => void;
  onDelete: () => void;
}) {
  const sense = item.senses?.[0];
  const image = item.images?.[0];
  const level = LEVELS.find(({ id }) => id === item.proficiencyLevel);
  const question = editedQuestion ?? item.adminQuestionOverrides?.[mode] ?? buildQuestionPreview(mode, item, items, index);
  return (
    <article className="curriculum-detail-card">
      <div className="curriculum-item-number"><small>{itemLabel}</small>{index + 1}</div>
      <div className="curriculum-item-image">{image ? <img src={image.thumbnail256Url || image.mobile512Url} alt={`Vocabulary concept for ${item.englishWord}`} /> : <span>No image</span>}</div>
      <div className="curriculum-item-content">
        <div className="word-row curriculum-word-row">
          <div><h3>{item.englishWord}</h3><p className="pronunciation">{item.pronunciation} · {item.partOfSpeech}</p></div>
          <span className="status-pill active curriculum-level-pill">{level?.title ?? 'Uncategorized'}</span>
        </div>
        <div className="definition-grid definition-grid-three">
          <div><small>Japanese word</small><p lang="ja">{sense?.japaneseTranslation || 'Missing'}</p></div>
          <div><small>Definition</small><p>{sense?.definition || 'Missing'}</p></div>
          <div><small>Japanese definition</small><p lang="ja">{sense?.japaneseDefinition || 'Missing'}</p></div>
        </div>
        <section className="curriculum-answer-key" aria-label={`Answer key for ${item.englishWord}`}>
          {editing && <button className="danger curriculum-delete-question" type="button" onClick={onDelete}>Delete question</button>}
          <div className="curriculum-question-heading">
            <div><small>Question type</small>{editing ? <input value={question.type} onChange={(event) => onQuestionChange({ ...question, type: event.target.value })} /> : <strong>{question.type}</strong>}</div>
            <div><small>Prompt / translation</small>{editing ? <textarea rows={2} value={question.prompt} onChange={(event) => onQuestionChange({ ...question, prompt: event.target.value })} /> : <strong>{question.prompt}</strong>}</div>
          </div>
          <div className="curriculum-choice-grid">
            {question.choices.map((choice, choiceIndex) => (
              <div className={`curriculum-choice ${choice === question.answer ? 'correct' : ''}`} key={`${choice}-${choiceIndex}`}>
                <span>{String.fromCharCode(65 + choiceIndex)}</span>
                {editing
                  ? <input value={choice} onChange={(event) => {
                    const choices = [...question.choices];
                    choices[choiceIndex] = event.target.value;
                    onQuestionChange({ ...question, choices });
                  }} />
                  : <strong>{choice}</strong>}
                {choice === question.answer && <small>Correct answer</small>}
              </div>
            ))}
          </div>
          <div className="curriculum-answer">
            <small>Answer</small>
            {editing
              ? <select value={question.answer} onChange={(event) => onQuestionChange({ ...question, answer: event.target.value })}>{question.choices.map((choice, choiceIndex) => <option value={choice} key={`${choice}-${choiceIndex}`}>{choice || `Choice ${choiceIndex + 1}`}</option>)}</select>
              : <strong>{question.answer}</strong>}
          </div>
        </section>
        <div className="curriculum-audio-grid"><AudioPreview label="American audio" url={item.audioAmericanUrl} /><AudioPreview label="British audio" url={item.audioBritishUrl} /></div>
        <code>{item.vocabId}</code>
      </div>
    </article>
  );
}

interface QuestionPreview {
  type: string;
  prompt: string;
  choices: string[];
  answer: string;
}

function buildQuestionPreview(
  mode: CurriculumMode,
  item: CurriculumVocabularyItem,
  items: CurriculumVocabularyItem[],
  index: number,
): QuestionPreview {
  const japaneseAnswer = item.senses?.[0]?.japaneseTranslation || 'Missing Japanese translation';
  const englishChoices = buildChoices(items, index, (candidate) => candidate.englishWord);
  const japaneseChoices = buildChoices(items, index, (candidate) => candidate.senses?.[0]?.japaneseTranslation || 'Missing translation');

  if (mode === 'spelling') {
    return { type: 'Listening · spelling', prompt: 'Listen to the audio and choose the correct spelling.', choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'vocabulary') {
    return { type: 'English → Japanese', prompt: `What is the Japanese meaning of “${item.englishWord}”?`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  if (mode === 'tournament') {
    return { type: 'Japanese → English', prompt: `Choose the English translation of “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'test') {
    return index % 2 === 0
      ? { type: 'English → Japanese', prompt: `Choose the Japanese translation of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer }
      : { type: 'Japanese → English', prompt: `Choose the English word for “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'flashcards') {
    return { type: 'Flashcard recall', prompt: `Recall the Japanese meaning of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  if (mode === 'listening' || mode === 'dictation') {
    return { type: mode === 'listening' ? 'Listening comprehension' : 'Audio dictation', prompt: 'Listen to the audio and choose the word you hear.', choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'translation') {
    return { type: 'Japanese → English', prompt: `Choose the natural English translation of “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'pronunciation') {
    return { type: 'Pronunciation check', prompt: `Say “${item.englishWord}” aloud.`, choices: [item.englishWord], answer: item.englishWord };
  }
  if (mode === 'reading' || mode === 'ai') {
    return { type: mode === 'reading' ? 'Reading comprehension' : 'Language question', prompt: `Which Japanese meaning best matches “${item.englishWord}”?`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  if (mode === 'mistakes') {
    return { type: 'Mistake review', prompt: `Review “${item.englishWord}” and choose its meaning.`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  if (mode === 'offline') {
    return { type: 'Downloaded vocabulary', prompt: `Choose the Japanese meaning of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  if (mode.startsWith('assessment-')) {
    const skill = mode.replace('assessment-', '');
    if (skill === 'listening') {
      return { type: 'Listening level check', prompt: 'Listen to the audio and choose the word you hear.', choices: englishChoices, answer: item.englishWord };
    }
    if (skill === 'writing') {
      return { type: 'Writing level check', prompt: `Choose the correct English spelling for “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
    }
    if (skill === 'speaking') {
      return { type: 'Speaking level check', prompt: `Say the English word for “${japaneseAnswer}”.`, choices: [item.englishWord], answer: item.englishWord };
    }
    return index % 2 === 0
      ? { type: `${humanizePreviewLabel(skill)} level check`, prompt: `Choose the Japanese meaning of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer }
      : { type: `${humanizePreviewLabel(skill)} level check`, prompt: `Choose the English word for “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
  }
  if (mode === 'ielts-academic' || mode === 'ielts-general' || mode === 'toeic' || mode === 'toefl') {
    return {
      type: `${mode.toUpperCase()} vocabulary`,
      prompt: `Choose the meaning of “${item.englishWord}” in this exam-readiness question.`,
      choices: japaneseChoices,
      answer: japaneseAnswer,
    };
  }
  if (mode === 'today') {
    return index % 2 === 0
      ? { type: "Today's vocabulary", prompt: `Choose the meaning of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer }
      : { type: "Today's translation", prompt: `Choose the English word for “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
  }
  const mixedType = index % 3;
  if (mixedType === 0) {
    return { type: 'Listening · spelling', prompt: 'Listen to the audio and choose the correct spelling.', choices: englishChoices, answer: item.englishWord };
  }
  if (mixedType === 1) {
    return { type: 'English → Japanese', prompt: `Choose the Japanese meaning of “${item.englishWord}”.`, choices: japaneseChoices, answer: japaneseAnswer };
  }
  return { type: 'Japanese → English', prompt: `Choose the English translation of “${japaneseAnswer}”.`, choices: englishChoices, answer: item.englishWord };
}

function humanizePreviewLabel(value: string): string {
  if (value === 'beginner') return 'Basic';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function buildChoices(
  items: CurriculumVocabularyItem[],
  correctIndex: number,
  selectValue: (item: CurriculumVocabularyItem) => string,
): string[] {
  const correct = selectValue(items[correctIndex]);
  const ordered = Array.from({ length: items.length }, (_, offset) => items[(correctIndex + offset) % items.length])
    .map(selectValue)
    .filter((value, index, values) => value && values.indexOf(value) === index);
  const choices = ordered.slice(0, CHOICE_COUNT);
  const answerIndex = Math.min(correctIndex % Math.max(choices.length, 1), choices.length - 1);
  const withoutCorrect = choices.filter((choice) => choice !== correct);
  withoutCorrect.splice(answerIndex, 0, correct);
  return withoutCorrect.slice(0, CHOICE_COUNT);
}

function AudioPreview({ label, url }: { label: string; url?: string }) {
  return <div><strong>{label}</strong>{url ? <audio controls preload="none" src={url} /> : <span>Missing</span>}</div>;
}

function curriculumRouteFromHash(hash: string, expectedMode: CurriculumMode): CurriculumRoute {
  const [, mode, level, set] = hash.replace(/^#\/?/, '').split('/');
  if (mode !== expectedMode || !isLevel(level)) return {};
  const setNumber = Number(set);
  return { level, setNumber: Number.isInteger(setNumber) && setNumber > 0 ? setNumber : undefined };
}

function navigateToCurriculum(mode: CurriculumMode, route: CurriculumRoute): void {
  const suffix = route.level ? `/${route.level}${route.setNumber ? `/${route.setNumber}` : ''}` : '';
  window.history.pushState(null, '', `#content/${mode}${suffix}`);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function curriculumModeFromHash(hash: string): CurriculumMode {
  const [, mode] = hash.replace(/^#\/?/, '').split('/');
  return mode in MODE_DETAILS ? mode as CurriculumMode : 'spelling';
}

function isLevel(value?: string): value is Level {
  return LEVELS.some(({ id }) => id === value);
}
